import React, { useState, useEffect } from 'react';
import {
    Scan,
    TrendingUp,
    Users,
    Gavel,
    ArrowRight,
    ShieldCheck,
    Zap,
    PlayCircle,
    Hexagon,
    ChevronRight,
    BarChart,
    Activity,
    Globe,
    Wallet,
    Compass
} from 'lucide-react';
import HeroCarousel from './HeroCarousel';

interface LandingPageProps {
    onEnter: () => void;
}

const LandingPage: React.FC<LandingPageProps> = ({ onEnter }) => {
    const [scrolled, setScrolled] = useState(false);
    const [activeTab, setActiveTab] = useState<'home' | 'market' | 'vault' | 'social'>('home');

    useEffect(() => {
        const handleScroll = () => {
            setScrolled(window.scrollY > 50);

            // Auto-highlight active tab on scroll
            const sections = ['home', 'market', 'vault', 'social'];
            for (const section of sections) {
                const el = document.getElementById(section);
                if (el) {
                    const rect = el.getBoundingClientRect();
                    if (rect.top >= 0 && rect.top <= window.innerHeight / 2) {
                        setActiveTab(section as any);
                    }
                }
            }
        };
        window.addEventListener('scroll', handleScroll);
        return () => window.removeEventListener('scroll', handleScroll);
    }, []);

    const scrollToSection = (id: string) => {
        const el = document.getElementById(id);
        if (el) {
            el.scrollIntoView({ behavior: 'smooth' });
            setActiveTab(id as any);
        }
    };

    return (
        <div className="min-h-screen bg-[#020617] text-white font-sans overflow-x-hidden selection:bg-cyan-500/30 selection:text-cyan-200 scroll-smooth">

            {/* --- BACKGROUND FX --- */}
            <div className="fixed inset-0 pointer-events-none overflow-hidden z-0">
                <div className="absolute top-[-20%] left-[-10%] w-[70vw] h-[70vw] bg-blue-900/10 rounded-full blur-[120px] animate-pulse delay-1000" />
                <div className="absolute bottom-[-20%] right-[-10%] w-[70vw] h-[70vw] bg-teal-900/10 rounded-full blur-[120px] animate-pulse delay-700" />
                <div className="absolute inset-0 bg-[linear-gradient(rgba(255,255,255,0.02)_1px,transparent_1px),linear-gradient(90deg,rgba(255,255,255,0.02)_1px,transparent_1px)] bg-[size:4rem_4rem] [mask-image:radial-gradient(ellipse_60%_60%_at_50%_50%,#000_70%,transparent_100%)]" />
            </div>

            {/* --- NAVIGATION --- */}
            <nav className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${scrolled ? 'bg-[#020617]/90 backdrop-blur-xl border-b border-white/5 py-4' : 'bg-transparent py-6'}`}>
                <div className="max-w-7xl mx-auto px-6 flex items-center justify-between">
                    <div
                        onClick={() => scrollToSection('home')}
                        className="flex items-center gap-2 cursor-pointer group"
                    >
                        <div className="relative w-8 h-8">
                            <div className="absolute inset-0 bg-gradient-to-tr from-cyan-500 to-blue-600 rounded-lg transform rotate-6 opacity-75 group-hover:rotate-12 transition-transform duration-500" />
                            <div className="relative bg-slate-900 w-full h-full rounded-lg border border-white/10 flex items-center justify-center">
                                <Zap className="w-4 h-4 text-cyan-400" />
                            </div>
                        </div>
                        <span className="font-bold text-xl tracking-tight select-none mr-3">
                            Poké<span className="text-transparent bg-clip-text bg-gradient-to-r from-cyan-400 to-blue-400">Base</span>
                        </span>
                        <span className="px-2 py-0.5 rounded text-[10px] font-bold bg-teal-500/20 text-teal-300 border border-teal-500/30 uppercase tracking-widest hidden sm:inline-block">
                            Early Access
                        </span>
                    </div>

                    <div className="hidden md:flex items-center gap-1 p-1 bg-white/5 rounded-full border border-white/5 backdrop-blur-md">
                        {['home', 'market', 'vault', 'social'].map((tab) => (
                            <button
                                key={tab}
                                onClick={() => scrollToSection(tab)}
                                className={`px-4 py-1.5 rounded-full text-sm font-medium transition-all ${activeTab === tab ? 'bg-white text-slate-900 shadow-lg' : 'text-slate-400 hover:text-white hover:bg-white/5'}`}
                            >
                                {tab.charAt(0).toUpperCase() + tab.slice(1)}
                            </button>
                        ))}
                    </div>

                    <div className="flex items-center gap-4">
                        <button
                            onClick={onEnter}
                            className="group relative px-6 py-2.5 bg-gradient-to-r from-cyan-500 to-blue-600 hover:from-cyan-400 hover:to-blue-500 text-white font-bold rounded-full overflow-hidden transition-all shadow-lg shadow-cyan-500/20 hover:shadow-cyan-500/40 active:scale-95"
                        >
                            <span className="relative flex items-center gap-2">
                                Launch Application <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
                            </span>
                        </button>
                    </div>
                </div>
            </nav>

            {/* --- HERO SECTION (HOME) --- */}
            <section id="home" className="relative min-h-screen flex items-center pt-24 px-6 z-10">
                <div className="max-w-7xl mx-auto w-full grid lg:grid-cols-2 gap-16 items-center">

                    <div className="space-y-8 animate-in fade-in slide-in-from-bottom-5 duration-1000">
                        <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-cyan-500/10 border border-cyan-500/20 text-cyan-400 text-xs font-bold uppercase tracking-widest">
                            <span className="w-2 h-2 rounded-full bg-cyan-400 animate-pulse" />
                            System v2.0 Online
                        </div>

                        <h1 className="text-5xl lg:text-7xl font-black tracking-tight leading-[1.1]">
                            Next-Gen <br />
                            <span className="text-transparent bg-clip-text bg-gradient-to-r from-cyan-400 via-blue-500 to-teal-600 animate-text-shimmer bg-[200%_auto]">
                                TCG Intelligence
                            </span>
                        </h1>

                        <p className="text-lg text-slate-400 max-w-xl leading-relaxed">
                            Track real-time market data across global exchanges. Manage your vault with AI-powered scanning. Connect with elite traders. All in one terminal.
                        </p>

                        <div className="flex flex-col sm:flex-row gap-4 pt-4">
                            <button
                                onClick={onEnter}
                                className="px-10 py-5 bg-white text-slate-900 hover:bg-slate-200 rounded-xl font-bold text-xl shadow-[0_0_20px_rgba(255,255,255,0.3)] transition-all hover:-translate-y-1 flex items-center justify-center gap-2 group"
                            >
                                <Scan className="w-5 h-5" />
                                Scan Now
                                <ChevronRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
                            </button>

                            <button
                                onClick={() => scrollToSection('market')}
                                className="px-8 py-4 bg-transparent border border-white/10 hover:border-white/20 hover:bg-white/5 text-slate-300 hover:text-white rounded-xl font-bold text-lg transition-all flex items-center justify-center gap-2"
                            >
                                Explore Features
                            </button>
                        </div>

                        <div className="pt-8 flex items-center gap-6 text-slate-500 text-sm font-medium border-t border-white/5 mt-8">
                            <div className="flex items-center gap-2">
                                <ShieldCheck className="w-4 h-4 text-emerald-500" /> Secure Encryption
                            </div>
                            <div className="flex items-center gap-2">
                                <Zap className="w-4 h-4 text-amber-500" /> Real-time TCGPlayer Sync
                            </div>
                        </div>
                    </div>

                    {/* Hero Visual - "Holo Card" */}
                    <div className="hidden lg:flex items-center justify-center perspective-[2000px] group">
                        <HeroCarousel />
                    </div>
                </div>
            </section>

            {/* --- MARKET SECTION --- */}
            <section id="market" className="py-32 relative z-10 bg-slate-950/50 border-t border-white/5 scroll-mt-20">
                <div className="max-w-7xl mx-auto px-6">
                    <div className="grid lg:grid-cols-2 gap-16 items-center">
                        {/* Visual */}
                        <div className="relative order-2 lg:order-1 h-[400px] bg-slate-900 border border-slate-800 rounded-2xl p-6 overflow-hidden group hover:border-cyan-500/30 transition-colors">
                            <div className="absolute inset-0 bg-grid-slate-800/[0.2] bg-[size:20px_20px]" />

                            {/* Fake Chart */}
                            <div className="absolute bottom-0 left-0 right-0 h-[60%] flex items-end justify-between px-8 pb-8 gap-2">
                                {[40, 65, 45, 80, 55, 90, 70, 85, 95, 60, 75, 100].map((h, i) => (
                                    <div
                                        key={i}
                                        className="w-full bg-cyan-500/20 rounded-t-sm relative group-hover:bg-cyan-500/40 transition-colors duration-500"
                                        style={{ height: `${h}%` } as React.CSSProperties}
                                    >
                                        <div className="absolute top-0 w-full h-1 bg-cyan-400 shadow-[0_0_10px_#22d3ee]" />
                                    </div>
                                ))}
                            </div>

                            {/* Ticker Overlay */}
                            <div className="absolute top-6 left-6 right-6 flex justify-between items-start">
                                <div>
                                    <h3 className="text-slate-400 text-xs font-bold uppercase tracking-widest mb-1">Market Index</h3>
                                    <div className="text-4xl font-mono font-bold text-white tracking-tighter">14,204.52</div>
                                    <div className="flex items-center text-emerald-400 text-sm font-bold mt-1">
                                        <TrendingUp className="w-4 h-4 mr-1" /> +2.4% (24h)
                                    </div>
                                </div>
                                <div className="flex gap-2">
                                    <div className="w-2 h-2 rounded-full bg-red-500 animate-pulse" />
                                    <div className="text-[10px] font-bold text-red-500 uppercase">Live Feed</div>
                                </div>
                            </div>
                        </div>

                        {/* Content */}
                        <div className="order-1 lg:order-2 space-y-6">
                            <div className="w-12 h-12 rounded-xl bg-cyan-500/10 flex items-center justify-center border border-cyan-500/20">
                                <BarChart className="w-6 h-6 text-cyan-400" />
                            </div>
                            <h2 className="text-3xl md:text-5xl font-black tracking-tight">
                                Global Market <br />Analytics
                            </h2>
                            <p className="text-slate-400 text-lg leading-relaxed">
                                Stop guessing. Get precise valuation data pulled directly from TCGPlayer and eBay sold listings. Visualize trends over time to identify the perfect moment to buy or sell.
                            </p>
                            <ul className="space-y-4 pt-4">
                                {[
                                    'Real-time price tracking across 50+ sets',
                                    'Historical price charts and volatility index',
                                    'Instant arbitrage opportunity alerts',
                                    'Verified transaction history ledger'
                                ].map((item, i) => (
                                    <li key={i} className="flex items-center gap-3 text-slate-300">
                                        <div className="w-1.5 h-1.5 rounded-full bg-cyan-500" />
                                        {item}
                                    </li>
                                ))}
                            </ul>
                        </div>
                    </div>
                </div>
            </section>

            {/* --- VAULT SECTION --- */}
            <section id="vault" className="py-32 relative z-10 scroll-mt-20">
                <div className="max-w-7xl mx-auto px-6">
                    <div className="grid lg:grid-cols-2 gap-16 items-center">
                        {/* Content */}
                        <div className="space-y-6">
                            <div className="w-12 h-12 rounded-xl bg-teal-500/10 flex items-center justify-center border border-teal-500/20">
                                <Wallet className="w-6 h-6 text-teal-400" />
                            </div>
                            <h2 className="text-3xl md:text-5xl font-black tracking-tight">
                                Military-Grade <br />Asset Vault
                            </h2>
                            <p className="text-slate-400 text-lg leading-relaxed">
                                Your collection deserves better than a spreadsheet. The Vault provides a secure, visually stunning interface to manage your assets, complete with AI-powered condition grading estimates.
                            </p>
                            <ul className="space-y-4 pt-4">
                                {[
                                    'Cloud-synced portfolio management',
                                    'Automated set completion tracking',
                                    'High-resolution card scanning via camera',
                                    'Privacy controls for public/private showcasing'
                                ].map((item, i) => (
                                    <li key={i} className="flex items-center gap-3 text-slate-300">
                                        <div className="w-1.5 h-1.5 rounded-full bg-teal-500" />
                                        {item}
                                    </li>
                                ))}
                            </ul>
                        </div>

                        {/* Visual - Scanner Interface */}
                        <div className="relative h-[480px] bg-slate-900 border border-teal-500/30 rounded-2xl overflow-hidden flex items-center justify-center shadow-2xl">
                            {/* Animated Background Mesh */}
                            <div className="absolute inset-0 bg-[radial-gradient(circle_at_center,rgba(20,184,166,0.15),transparent_70%)]" />
                            <div className="absolute inset-0 bg-[linear-gradient(rgba(20,184,166,0.1)_1px,transparent_1px),linear-gradient(90deg,rgba(20,184,166,0.1)_1px,transparent_1px)] bg-[size:30px_30px] opacity-30" />

                            {/* Centered TCG Card */}
                            <div className="relative z-10 w-[240px] aspect-[2.5/3.5] bg-slate-800 rounded-xl shadow-2xl transform rotate-2">
                                <img
                                    src="https://images.unsplash.com/photo-1613771404784-3a5686aa2be3?q=80&w=800&auto=format&fit=crop"
                                    alt="Scanned Pokémon Card"
                                    className="w-full h-full object-cover rounded-xl shadow-lg"
                                />
                                {/* Scanning Beam Effect */}
                                <div className="absolute inset-0 overflow-hidden rounded-xl">
                                    <div className="absolute top-0 left-0 right-0 h-[2px] bg-cyan-400 shadow-[0_0_20px_rgba(34,211,238,0.8)] animate-[scan-vertical_3s_ease-in-out_infinite]" />
                                    <div className="absolute inset-0 bg-gradient-to-b from-cyan-400/10 to-transparent animate-[scan-vertical_3s_ease-in-out_infinite] h-[20%]" />
                                </div>
                            </div>

                            {/* Viewfinder Overlay Widget */}
                            <div className="absolute inset-0 pointer-events-none flex items-center justify-center">
                                <div className="relative w-[300px] h-[440px] border border-teal-500/30 rounded-lg">

                                    {/* Corner Brackets */}
                                    <div className="absolute top-0 left-0 w-8 h-8 border-t-2 border-l-2 border-cyan-400 shadow-[0_0_10px_#22d3ee]" />
                                    <div className="absolute top-0 right-0 w-8 h-8 border-t-2 border-r-2 border-cyan-400 shadow-[0_0_10px_#22d3ee]" />
                                    <div className="absolute bottom-0 left-0 w-8 h-8 border-b-2 border-l-2 border-cyan-400 shadow-[0_0_10px_#22d3ee]" />
                                    <div className="absolute bottom-0 right-0 w-8 h-8 border-b-2 border-r-2 border-cyan-400 shadow-[0_0_10px_#22d3ee]" />

                                    {/* HUD Elements */}
                                    <div className="absolute top-4 left-1/2 -translate-x-1/2 px-3 py-1 bg-slate-950/80 border border-teal-500/50 rounded-full flex items-center gap-2 shadow-lg">
                                        <div className="w-2 h-2 bg-red-500 rounded-full animate-pulse" />
                                        <span className="text-[10px] font-mono text-teal-300 font-bold tracking-widest uppercase">Live Analysis</span>
                                    </div>

                                    {/* Side Stats */}
                                    <div className="absolute right-[-40px] top-1/4 flex flex-col gap-2">
                                        <div className="w-1 h-12 bg-teal-500/20 rounded-full overflow-hidden">
                                            <div className="w-full h-[60%] bg-teal-500 animate-pulse" />
                                        </div>
                                    </div>
                                </div>
                            </div>

                            {/* Floating Metadata Card */}
                            <div className="absolute bottom-8 bg-slate-950/90 backdrop-blur-xl border-l-2 border-cyan-400 p-4 rounded-r-xl shadow-2xl flex items-center gap-4 animate-in slide-in-from-bottom-4 duration-1000 z-20">
                                <div className="flex flex-col">
                                    <span className="text-[10px] text-slate-500 font-bold uppercase tracking-widest">Match Found</span>
                                    <span className="text-white font-bold font-mono">Charizard VMAX (Rainbow)</span>
                                </div>
                                <div className="h-8 w-px bg-white/10" />
                                <div className="flex flex-col items-end">
                                    <span className="text-[10px] text-slate-500 font-bold uppercase tracking-widest">Confidence</span>
                                    <span className="text-emerald-400 font-bold font-mono">99.8%</span>
                                </div>
                            </div>

                            <style>{`
                                @keyframes scan-vertical {
                                    0% { top: 0%; opacity: 0; }
                                    10% { opacity: 1; }
                                    90% { opacity: 1; }
                                    100% { top: 100%; opacity: 0; }
                                }
                            `}</style>
                        </div>
                    </div>
                </div>
            </section>

            {/* --- SOCIAL SECTION --- */}
            <section id="social" className="py-32 relative z-10 bg-slate-950/50 border-t border-white/5 scroll-mt-20">
                <div className="max-w-7xl mx-auto px-6">
                    <div className="grid lg:grid-cols-2 gap-16 items-center">

                        {/* Visual - Network Map */}
                        <div className="relative order-2 lg:order-1 h-[400px] bg-slate-900 border border-slate-800 rounded-2xl overflow-hidden group hover:border-emerald-500/30 transition-colors">
                            <div className="absolute inset-0 opacity-20 bg-[url('https://upload.wikimedia.org/wikipedia/commons/e/ec/World_map_blank_without_borders.svg')] bg-cover bg-center mix-blend-overlay invert" />

                            {/* Nodes */}
                            <div className="absolute top-1/4 left-1/4 w-3 h-3 bg-emerald-500 rounded-full shadow-[0_0_15px_#10b981] animate-pulse" />
                            <div className="absolute top-1/3 right-1/3 w-2 h-2 bg-emerald-500/50 rounded-full" />
                            <div className="absolute bottom-1/4 left-1/2 w-2 h-2 bg-emerald-500/50 rounded-full" />

                            {/* Connections */}
                            <svg className="absolute inset-0 w-full h-full pointer-events-none">
                                <line x1="25%" y1="25%" x2="66%" y2="33%" stroke="rgba(16, 185, 129, 0.2)" strokeWidth="1" />
                                <line x1="25%" y1="25%" x2="50%" y2="75%" stroke="rgba(16, 185, 129, 0.2)" strokeWidth="1" />
                            </svg>

                            {/* Active User Card */}
                            <div className="absolute bottom-6 right-6 bg-slate-950/90 border border-emerald-500/30 p-4 rounded-xl shadow-xl flex items-center gap-4">
                                <div className="w-10 h-10 rounded-full bg-emerald-500/20 flex items-center justify-center text-emerald-400">
                                    <Users className="w-5 h-5" />
                                </div>
                                <div>
                                    <div className="text-white font-bold text-sm">New Trade Offer</div>
                                    <div className="text-slate-400 text-xs">From: CryptoAsh_99</div>
                                </div>
                                <button className="px-3 py-1 bg-emerald-600 hover:bg-emerald-500 text-white text-xs font-bold rounded-lg transition-colors">
                                    View
                                </button>
                            </div>
                        </div>

                        {/* Content */}
                        <div className="order-1 lg:order-2 space-y-6">
                            <div className="w-12 h-12 rounded-xl bg-emerald-500/10 flex items-center justify-center border border-emerald-500/20">
                                <Globe className="w-6 h-6 text-emerald-400" />
                            </div>
                            <h2 className="text-3xl md:text-5xl font-black tracking-tight">
                                The Network <br />Effect
                            </h2>
                            <p className="text-slate-400 text-lg leading-relaxed">
                                Connect with serious collectors worldwide. The Social Hub is more than a chat room—it's a decentralized marketplace where reputation is key and deals are made.
                            </p>
                            <ul className="space-y-4 pt-4">
                                {[
                                    'Encrypted direct messaging',
                                    'Reputation scoring system for safe trades',
                                    'Community leaderboards and challenges',
                                    'Live stream unboxing integration'
                                ].map((item, i) => (
                                    <li key={i} className="flex items-center gap-3 text-slate-300">
                                        <div className="w-1.5 h-1.5 rounded-full bg-emerald-500" />
                                        {item}
                                    </li>
                                ))}
                            </ul>
                        </div>

                    </div>
                </div>

                {/* --- LIVE ACTIVITY TICKER (REMOVED: MOCK DATA) --- */}
            </section>

            {/* --- MEMBERSHIP TIERS --- */}
            <section id="pricing" className="py-32 relative z-10 scroll-mt-20">
                <div className="max-w-7xl mx-auto px-6">
                    <div className="text-center mb-20 space-y-4">
                        <h2 className="text-3xl md:text-5xl font-black tracking-tight">Select Your Clearance Level</h2>
                        <p className="text-slate-400 max-w-2xl mx-auto">From casual tracking to institutional-grade analytics. Choose the power you need.</p>
                    </div>

                    <div className="grid md:grid-cols-2 xl:grid-cols-4 gap-6">
                        {/* TIER 1: TRACKER */}
                        <div className="bg-slate-900/50 border border-slate-800 rounded-2xl p-6 hover:border-slate-600 transition-colors flex flex-col">
                            <div className="mb-4">
                                <span className="px-3 py-1 rounded-full bg-slate-800 text-slate-300 text-xs font-bold uppercase tracking-widest">Free</span>
                            </div>
                            <h3 className="text-2xl font-bold text-white mb-2">Tracker</h3>
                            <div className="text-slate-400 text-sm mb-6 h-10">Essential scanning and portfolio tracking.</div>
                            <div className="text-3xl font-bold text-white mb-6">$0<span className="text-lg text-slate-500 font-normal">/mo</span></div>

                            <ul className="space-y-3 mb-8 flex-1">
                                <li className="flex gap-2 text-sm text-slate-300"><Scan className="w-4 h-4 text-slate-500" /> AI Card Scanning</li>
                                <li className="flex gap-2 text-sm text-slate-300"><BarChart className="w-4 h-4 text-slate-500" /> Basic Price Tracking</li>
                                <li className="flex gap-2 text-sm text-slate-300"><Wallet className="w-4 h-4 text-slate-500" /> Portfolio Management</li>
                            </ul>
                            <button className="w-full py-3 rounded-lg border border-slate-700 hover:bg-slate-800 text-white font-bold transition-colors">Start Free</button>
                        </div>

                        {/* TIER 2: COLLECTOR */}
                        <div className="bg-slate-900/80 border border-blue-500/30 rounded-2xl p-6 hover:border-blue-500/60 transition-colors flex flex-col relative overflow-hidden group">
                            <div className="absolute top-0 left-0 w-full h-1 bg-gradient-to-r from-transparent via-blue-500 to-transparent opacity-50" />
                            <div className="mb-4">
                                <span className="px-3 py-1 rounded-full bg-blue-500/10 text-blue-300 text-xs font-bold uppercase tracking-widest border border-blue-500/20">Popular</span>
                            </div>
                            <h3 className="text-2xl font-bold text-white mb-2">Collector</h3>
                            <div className="text-slate-400 text-sm mb-6 h-10">Unlock the social network and encrypted comms.</div>
                            <div className="text-3xl font-bold text-white mb-6">$5<span className="text-lg text-slate-500 font-normal">/mo</span></div>

                            <ul className="space-y-3 mb-8 flex-1">
                                <li className="flex gap-2 text-sm text-white"><ShieldCheck className="w-4 h-4 text-blue-400" /> <b>Encrypted Messaging</b></li>
                                <li className="flex gap-2 text-sm text-white"><Globe className="w-4 h-4 text-blue-400" /> <b>Social Network Access</b></li>
                                <li className="flex gap-2 text-sm text-slate-300"><Users className="w-4 h-4 text-slate-500" /> Community Groups</li>
                                <li className="flex gap-2 text-sm text-slate-300"><Zap className="w-4 h-4 text-slate-500" /> Priority Support</li>
                            </ul>
                            <button className="w-full py-3 rounded-lg bg-blue-600 hover:bg-blue-500 text-white font-bold transition-colors shadow-lg shadow-blue-500/20">Upgrade</button>
                        </div>

                        {/* TIER 3: TRADER */}
                        <div className="bg-slate-900/50 border border-teal-500/30 rounded-2xl p-6 hover:border-teal-500/60 transition-colors flex flex-col">
                            <div className="mb-4">
                                <span className="px-3 py-1 rounded-full bg-teal-500/10 text-teal-300 text-xs font-bold uppercase tracking-widest border border-teal-500/20">Pro</span>
                            </div>
                            <h3 className="text-2xl font-bold text-white mb-2">Trader</h3>
                            <div className="text-slate-400 text-sm mb-6 h-10">Monetize your stream and build a following.</div>
                            <div className="text-3xl font-bold text-white mb-6">$14<span className="text-lg text-slate-500 font-normal">/mo</span></div>

                            <ul className="space-y-3 mb-8 flex-1">
                                <li className="flex gap-2 text-sm text-slate-300"><PlayCircle className="w-4 h-4 text-teal-400" /> <b>Live Streaming</b></li>
                                <li className="flex gap-2 text-sm text-slate-300"><Wallet className="w-4 h-4 text-teal-400" /> Monetization Tools</li>
                                <li className="flex gap-2 text-sm text-slate-300"><Zap className="w-4 h-4 text-teal-400" /> Verified Merchant Badge</li>
                                <li className="flex gap-2 text-sm text-slate-300"><Activity className="w-4 h-4 text-slate-500" /> Advanced Analytics</li>
                            </ul>
                            <button className="w-full py-3 rounded-lg border border-teal-500/50 hover:bg-teal-500/10 text-white font-bold transition-colors">Start Trial</button>
                        </div>

                        {/* TIER 4: INVESTOR */}
                        <div className="bg-slate-900/50 border border-emerald-500/30 rounded-2xl p-6 hover:border-emerald-500/60 transition-colors flex flex-col">
                            <div className="mb-4">
                                <span className="px-3 py-1 rounded-full bg-emerald-500/10 text-emerald-300 text-xs font-bold uppercase tracking-widest border border-emerald-500/20">Elite</span>
                            </div>
                            <h3 className="text-2xl font-bold text-white mb-2">Investor</h3>
                            <div className="text-slate-400 text-sm mb-6 h-10">Institutional-grade tools and auction access.</div>
                            <div className="text-3xl font-bold text-white mb-6">$29<span className="text-lg text-slate-500 font-normal">/mo</span></div>

                            <ul className="space-y-3 mb-8 flex-1">
                                <li className="flex gap-2 text-sm text-slate-300"><Gavel className="w-4 h-4 text-emerald-400" /> <b>Auction & Bidding</b></li>
                                <li className="flex gap-2 text-sm text-slate-300"><TrendingUp className="w-4 h-4 text-emerald-400" /> Business Dashboard</li>
                                <li className="flex gap-2 text-sm text-slate-300"><ShieldCheck className="w-4 h-4 text-emerald-400" /> API Access</li>
                                <li className="flex gap-2 text-sm text-slate-300"><Users className="w-4 h-4 text-slate-500" /> White-glove Support</li>
                            </ul>
                            <button className="w-full py-3 rounded-lg border border-emerald-500/50 hover:bg-emerald-500/10 text-white font-bold transition-colors">Contact Sales</button>
                        </div>
                    </div>
                </div>
            </section>

            {/* --- CTA FOOTER --- */}
            <section className="py-32 relative overflow-hidden bg-slate-950">
                <div className="absolute inset-0 bg-gradient-to-b from-[#020617] to-blue-950/20" />

                <div className="max-w-4xl mx-auto px-6 relative z-10 text-center">
                    <Hexagon className="w-16 h-16 text-cyan-500/50 mx-auto mb-8 animate-spin-slow" />

                    <h2 className="text-4xl md:text-6xl font-black tracking-tight mb-8">
                        Ready to Enter the System?
                    </h2>

                    <div className="flex flex-col items-center gap-6">
                        <p className="text-slate-400 max-w-md mx-auto">
                            Join the new standard of TCG management. Free access for early adopters.
                        </p>

                        <button
                            onClick={onEnter}
                            className="px-10 py-5 bg-white text-slate-950 rounded-2xl font-bold text-xl hover:scale-105 active:scale-95 transition-all shadow-[0_0_40px_rgba(255,255,255,0.3)] hover:shadow-[0_0_60px_rgba(255,255,255,0.5)]"
                        >
                            Initialize App
                        </button>

                        <div className="text-slate-600 text-xs font-mono uppercase tracking-widest mt-8">
                            PokéBase Systems © 2025
                        </div>
                    </div>
                </div>
            </section>

            <style>{`
        .shimmer-anim { animation: shimmer 2s infinite linear; }
        @keyframes shimmer {
          0% { transform: translateX(-100%); }
          100% { transform: translateX(100%); }
        }
        @keyframes text-shimmer {
          0% { background-position: 0% 50%; }
          100% { background-position: 100% 50%; }
        }
        @keyframes float-slow {
          0%, 100% { transform: translateY(0); }
          50% { transform: translateY(-10px); }
        }
        .animate-float-slow { animation: float-slow 6s ease-in-out infinite; }
        .delay-100 { animation-delay: 100ms; }
        .delay-500 { animation-delay: 500ms; }
        .delay-700 { animation-delay: 700ms; }
        .delay-1000 { animation-delay: 1000ms; }
      `}</style>

            {/* --- FEEDBACK WIDGET --- */}
            <button className="fixed bottom-6 right-6 z-50 px-4 py-3 bg-slate-900/90 backdrop-blur-xl border border-white/10 rounded-full shadow-2xl flex items-center gap-3 hover:bg-slate-800 transition-all hover:scale-105 group">
                <div className="relative">
                    <div className="w-2 h-2 bg-red-500 rounded-full absolute -top-0.5 -right-0.5 animate-pulse" />
                    <Compass className="w-5 h-5 text-slate-300 group-hover:text-white" />
                </div>
                <div className="flex flex-col items-start">
                    <span className="text-xs font-bold text-white">Feedback</span>
                    <span className="text-[10px] text-slate-400 leading-none">Help us improve</span>
                </div>
            </button>
        </div>
    );
};

export default LandingPage;
